import { Box, Button, ButtonProps, keyframes, styled } from "@mui/material";
import { memo } from "react";
import Row from "./Row";
import { MediaType } from "types/file";
import { formatImageUrl } from "services/utils/helpers";
import { checkFileIsImage, createDownloadLink } from "helpers/file";
import { AddCircleOutline, AttachFile } from "@mui/icons-material";
import { useLoading } from "providers/loading";
import { apiUploadFile } from "services/api/file";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { API_DOMAIN } from "constants/schemas";
import { useIntl } from "react-intl";
import { ArrowUpIcon } from "assets/icons";
import { Img } from "./Img";
import { UploadIcon } from "assets/icons/upload.icon";
import { showNotification } from "helpers/notification";

const ALLOWED_FILE_TYPES = [
	"image/png",
	"image/jpeg",
	"image/jpg",
	"image/webp",
];

const ALLOWED_EXTENSIONS = [
	"png",
	"jpeg",
	"jpg",
	"webp",
	"PNG",
	"JPEG",
	"JPG",
	"WEBP",
];

// File signatures for validation
const FILE_SIGNATURES = {
	'image/png': [0x89, 0x50, 0x4E, 0x47],
	'image/jpeg': [0xFF, 0xD8, 0xFF],
	'image/webp': [0x52, 0x49, 0x46, 0x46]
};

const checkFileSignature = async (file: File): Promise<boolean> => {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const arr = new Uint8Array(e.target?.result as ArrayBuffer).subarray(0, 4);
			const header = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
			
			// Check if the file signature matches any of the allowed types
			const isValid = Object.values(FILE_SIGNATURES).some(signature => {
				const signatureHex = signature.map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
				return header.startsWith(signatureHex);
			});
			
			resolve(isValid);
		};
		reader.readAsArrayBuffer(file);
	});
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 4;

type Props = {
	medias?: MediaType[];
	setMedias?: (medias?: any) => void;
	inputId?: string;
	signle?: boolean;
	signleMedia?: string;
	isPrivate?: boolean;
	disabled?: boolean;
};

const UploadFiles = ({
	medias = [],
	setMedias,
	inputId = "media-input",
	signle = false,
	signleMedia = "",
	isPrivate = false,
	disabled = false,
}: Props) => {
	const { setLoading, loading } = useLoading();
	const t = useIntl();

	const onClickUploadFile = () => {
		if (!disabled) {
			document.getElementById(inputId)?.click();
		}
	};

	const handleFileChange = async (event: any, type: string) => {
		const file = event.target.files[0];
		if (file) {
			const fileExtension = file.name.split(".").pop()?.toLowerCase();

			// Check file type and extension
			if (
				!ALLOWED_FILE_TYPES.includes(file.type) ||
				!ALLOWED_EXTENSIONS.includes(fileExtension || "")
			) {
				showNotification(
					t.formatMessage(
						{
							id: "file_type_not_supported",
						},
						{
							types: ALLOWED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(
								", "
							),
						}
					)
				);
				// Reset file input
				event.target.value = "";
				return;
			}

			// Additional file signature validation
			const isValidSignature = await checkFileSignature(file);
			if (!isValidSignature) {
				showNotification(
					t.formatMessage(
						{
							id: "file_type_not_supported",
						},
						{
							types: ALLOWED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(
								", "
							),
						}
					)
				);
				event.target.value = "";
				return;
			}

			// Check file size
			if (file.size > MAX_FILE_SIZE) {
				showNotification(
					t.formatMessage(
						{
							id: "file_type_less_than",
						},
						{
							numberMb: "5mb",
						}
					)
				);
				// Reset file input
				event.target.value = "";
				return;
			}

			// Check number of files
			if (!signle && medias.length >= MAX_FILES) {
				showNotification(
					t.formatMessage(
						{
							id: "file_upload_limit_exceeded",
						},
						{
							numberfile: "4",
						}
					)
				);
				// Reset file input
				event.target.value = "";
				return;
			}

			setLoading(true);
			let formData = new FormData();
			formData.append("file", file);
			if (isPrivate) {
				formData.append("is_private", "true");
			}

			// Validate file name
			const fileName = file.name.trim();
			if (!fileName) {
				showNotification(t.formatMessage({ id: "invalid_file_name" }));
				setLoading(false);
				event.target.value = "";
				return;
			}

			apiUploadFile(formData)
				.then((res) => {
					let { file_url, name } = res.data?.message || {};

					// Validate response data
					if (!file_url || !name) {
						showNotification(
							t.formatMessage({ id: "invalid_upload_response" })
						);
						setLoading(false);
						event.target.value = "";
						return;
					}

					let newMedias = undefined;
					if (signle) {
						setMedias?.(file_url);
					} else {
						let newMedia: MediaType = {
							url: file_url,
							file: name,
						};
						newMedias = [...medias, newMedia];
						setMedias?.(newMedias);
					}
					setLoading(false);
					// Reset file input after successful upload
					event.target.value = "";
				})
				.catch((err) => {
					console.log(err);
					let errorMessage = t.formatMessage({
						id: "error_upload_file_up_server",
					});

					// Handle specific error cases
					if (err.response?.data?.message) {
						const serverMessage = err.response.data.message;
						if (typeof serverMessage === "string") {
							try {
								const parsedMessage = JSON.parse(serverMessage);
								if (parsedMessage.title && parsedMessage.message) {
									errorMessage = parsedMessage.message;
								}
							} catch (e) {
								// If parsing fails, use the original message
								errorMessage = serverMessage;
							}
						}
					}

					showNotification(errorMessage);
					setLoading(false);
					// Reset file input after error
					event.target.value = "";
				});
		}
	};

	const onClickClear = (e: any, i?: number) => {
		e.stopPropagation();
		if (signle) {
			setMedias?.(null);
		} else {
			const newMedias = medias.filter((_, id) => id !== i);
			setMedias?.(newMedias);
		}
	};

	const onClickMedia = (mediaUrl: string) => {
		if (!disabled) {
			if (mediaUrl) {
				let link = `${API_DOMAIN}${mediaUrl}`;
				createDownloadLink(link, mediaUrl);
			} else {
				onClickUploadFile();
			}
		}
	};

	return (
		<StyledRow flexWrap="wrap">
			{(signle ? [{ url: signleMedia }] : medias).map((media, i) => {
				if (media.url)
					return (
						<Box
							className="app_media_box"
							key={i}
							onClick={() => onClickMedia(media.url)}
						>
							{media.url ? (
								checkFileIsImage(media.url) ? (
									<Img src={formatImageUrl(media.url, 1)} />
								) : (
									<AttachFile />
								)
							) : (
								signle && <AddCircleOutline />
							)}

							{media.url && !disabled && (
								<IoMdCloseCircleOutline
									className="clear-icon"
									onClick={(e) => onClickClear(e, i)}
								/>
							)}
						</Box>
					);
				return (
					<UploadButton
						loading={loading}
						onClickUploadFile={onClickUploadFile}
						key={i}
					/>
				);
			})}

			{!signle && (
				<UploadButton loading={loading} onClickUploadFile={onClickUploadFile} />
			)}
			<input
				id={inputId}
				className="hidden"
				type="file"
				onChange={(e) => handleFileChange(e, "medias")}
			/>
		</StyledRow>
	);
};

type buttonPropsType = {
	onClickUploadFile: () => void;
	loading: boolean;
};
const UploadButton = (props: buttonPropsType) => {
	const { onClickUploadFile, loading } = props;

	return (
		<div
			onClick={onClickUploadFile}
			className={`container-upload-img ${loading && "uploading"}`}
		>
			<UploadIcon color="green" />
		</div>
	);
};

const uploadEffect = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const StyledRow = styled(Row)(({ theme }) => ({
	".app_media_box": {
		position: "relative",
	},
	".clear-icon": {
		position: "absolute",
		right: 2,
		color: "#000",
		cursor: "pointer",
		top: 2,
		fontSize: 24,
	},
	".upload-btn": {
		fontSize: 14,
		fontWeight: 500,
		backgroundColor: "var(--gray-100)",
		padding: "8px 10px",
		borderRadius: 16,
		color: "var(--gray-700)",
		textTransform: "initial",
		lineHeight: "20px",
		svg: {
			width: 10,
			heigth: 10,
		},
	},
	".container-upload": {
		display: "flex",
		flexDirection: "row",
		height: "100%",
		gap: "16px",
		flexWrap: "wrap",
		marginBottom: "12px",
	},
	".container-upload-img": {
		textAlign: "center",
		border: "1px dotted black",
		borderRadius: "8px",
		padding: "12px !important",
		// marginLeft: '16px',
		cursor: "pointer",
		width: 60,
		height: 60,
	},
	".uploading": {
		background: "linear-gradient(90deg, #e0f7fa 25%, #80deea 50%, #e0f7fa 75%)",
		backgroundSize: "200% 100%",
		animation: `${uploadEffect} 1.5s infinite`,
	},
	[theme.breakpoints.down("md")]: {
		".upload-btn": {
			fontSize: 12,
		},
	},
}));

export default memo(UploadFiles);
